<template>
  <div>
    <!-- Category loop -->
    <div v-for="cat in categories" :key="cat.Slug" :id="'category-' + cat.Slug" class="category-block mb-12">
      <!-- Cat header -->
      <h2 class="text-md font-normal mb-4 flex items-baseline gap-2">
        <NuxtLink
          :to="categoryLink(cat.Slug)"
          class="inline-flex items-baseline gap-2 group"
        >
          <span class="group-hover:underline">{{ cat.Name }}</span>
        </NuxtLink>
      </h2>

      <div class="flex flex-wrap gap-x-15 gap-y-14">
        <!-- Model loop -->
        <div v-for="model in cat.ProductModels" :key="model.Style" class="flex flex-col w-full md:w-auto">
          <div class="h-8 flex flex-wrap items-center gap-3 text-xs uppercase tracking-wide text-neutral-500">
            <div v-if="model.New" class="inline-block mb-2">
              <span class="border px-2 py-1 text-workbook-black">New style</span>
            </div>
            <div v-else-if="model.NewColour" class="inline-block mb-2">
              <span class="border border-workbook-black px-2 py-1 text-workbook-black">New colour</span>
            </div>
            <div v-else-if="model.Revised" class="inline-block mb-2">
              <span class="border border-workbook-black px-2 py-1 text-workbook-black">Revised</span>
            </div>
          </div>

          <div class="flex items-center mb-3 h-9">
            <h3 class="text-sm font-semibold leading-tight mb-0">
              {{ model.name }}
            </h3>
            <ModelSaveToggle
              v-if="!model.NOTAvailableforPREOrder && !isPrint"
              :showLabel="false"
              :model="model"
              size="sm"
              shape="square"
              class="mr-4 text-xs"
            />
            
            <!-- View Notes Badge with Popover -->
            <UPopover class="ml-auto" v-if="!isPrint && notesStore.hasNoteForStyle(model.Style)">
              <span class="cursor-pointer border-1 uppercase border-[#C5C089] text-xs rounded-xs bg-workbook-yellow px-2 py-1  text-workbook-black">View notes</span>
              <template #content>
                <div class="p-4 w-96">
                  <h4 class="font-semibold mb-2">Notes:</h4>
                  <UTextarea 
                    :model-value="notesStore.getNoteForStyle(model.Style)"
                    :rows="5" 
                    placeholder="Add your notes about this product here..."
                    class="w-full"
                    @update:model-value="(val: string) => onNoteChange(model.Style, val)"
                  />
                </div>
              </template>
            </UPopover>
          </div>

          <!-- PRICING (only show when isPrint is true) -->
          <div v-if="isPrint" class="text-xs mb-3">
            WS: {{ formatNumber(getWsPrice(model), settings.currency) }}
            <span class="ml-2">MSRP: {{ formatNumber(getMsrpPrice(model), settings.currency) }}</span>
          </div>

          <div class="w-full"
            :class="[
              'sm:grid sm:grid-cols-1 lg:flex gap-1',
              showQuantities ? 'lg:flex-nowrap' : 'lg:flex-nowrap',
              isPrint ? 'print:overflow-visible print:flex-wrap' : ''
            ]"
          >
            <template v-for="(color, colorIdx) in uniqueColors(model)" :key="model.Style + '-' + color">
              <ModelColorCard
                v-motion-pop
                :model="model"
                :color="color"
                :showModelName="false"
                :showSaveInHeader="false"
                :showQuantities="showQuantities"
                card-width="sm"
                :getGlobalTabIndexForSku="getGlobalTabIndexForSku"
              />
            </template>
          </div>

          <!-- Notes display for print -->
          <div v-if="isPrint && notesStore.hasNoteForStyle(model.Style)" class="mt-4">
            <h4 class="text-sm font-semibold mb-1">Notes:</h4>
            <p class="text-xs leading-snug whitespace-pre-line mb-0">{{ notesStore.getNoteForStyle(model.Style) }}</p>
          </div>
        </div>
        <!-- End model loop -->
      </div>
      
    </div>
    <!-- End category loop -->
  </div>
</template>

<script setup lang="ts">
import type { PropType } from 'vue'
import { computed } from 'vue'
import type { CategoryModel, ProductModel } from '@/types/catalog'
import ModelSaveToggle from '@/components/cart/ModelSaveToggle.vue'
import ModelColorCard from '@/components/workbook/ModelColorCard.vue'
import { useSettingsStore } from '@/store/settings'
import { useNotesStore } from '@/store/notes'
import { CANONICAL_ORDER } from '@/utils/sizes'
import { formatNumber } from '@/utils/formatNumber'

const props = defineProps({
  categories: { type: Array as PropType<CategoryModel[]>, required: true },
  isPrint: { type: Boolean, default: false },
  showQuantities: { type: Boolean, default: false }
})

const settings = useSettingsStore()
settings.hydrate()

const notesStore = useNotesStore()
notesStore.hydrate()

const isPrint = computed(() => props.isPrint)
const showQuantities = computed(() => props.showQuantities)

const workbookMode = computed(() => settings.workbookMode || 'All')

// Bygg rätt länk per kategori
function categoryLink(slug: string) {
  if (workbookMode.value === 'All') {
    return `/products/${slug}`
  }
  return `/products/${slug}?category=${encodeURIComponent(workbookMode.value.toLowerCase())}`
}

// Debounced save function for notes
let saveTimeout: NodeJS.Timeout | null = null
function onNoteChange(style: string | number, value: string) {
  if (saveTimeout) clearTimeout(saveTimeout)
  saveTimeout = setTimeout(() => {
    if (value.trim()) {
      notesStore.setNote(style, value)
    } else {
      notesStore.deleteNote(style)
    }
  }, 500)
}

function uniqueColors(model: ProductModel): string[] {
  const seen: string[] = []
  for (const v of model.variants) {
    if (!seen.includes(v.Colour)) seen.push(v.Colour)
  }
  return seen
}

// Calculate WS price for a model based on current currency
function getWsPrice(model: ProductModel): number {
  switch (settings.currency) {
    case 'USD': return Number(model.WSUSD)
    case 'EUR': return Number(model.WSEUR)
    case 'GBP': return Number(model.WSGBP)
    case 'CHF': return Number(model.WSCHF)
    case 'CAD':
    default:
      return Number(model.WSCAD)
  }
}

// Calculate MSRP price for a model based on current currency
function getMsrpPrice(model: ProductModel): number {
  switch (settings.currency) {
    case 'USD': return Number(model.MSRPUSD)
    case 'EUR': return Number(model.MSRPEUR)
    case 'GBP': return Number(model.MSRPGBP)
    case 'CHF': return Number(model.MSRPCHF)
    case 'CAD':
    default:
      return Number(model.MSRPCAD)
  }
}

// GLOBAL TABINDEX IMPLEMENTATION
const tabIndexArr: { sku: string, idx: number }[] = []
let globalTabIdx = 1
for (const cat of props.categories) {
  for (const model of cat.ProductModels) {
    for (const color of uniqueColors(model)) {
      // Sortera storlekar i CANONICAL_ORDER
      const sizeRows: { sku: string, displaySize: string }[] = []
      for (const v of model.variants) {
        if (v.Colour !== color) continue
        if (!v.SKU) continue
        const normSize = normalizeSizeCode(v.SizeCode || v.Size || '')
        sizeRows.push({ sku: v.SKU, displaySize: normSize })
      }
      sizeRows.sort((a, b) => {
        const ai = CANONICAL_ORDER.indexOf(a.displaySize as any)
        const bi = CANONICAL_ORDER.indexOf(b.displaySize as any)
        if (ai !== bi) return ai - bi
        return a.displaySize.localeCompare(b.displaySize)
      })
      for (const row of sizeRows) {
        tabIndexArr.push({ sku: row.sku, idx: globalTabIdx++ })
      }
    }
  }
}

// Pass as prop to ModelColorCard: getGlobalTabIndexForSku
function getGlobalTabIndexForSku(sku: string): number {
  const obj = tabIndexArr.find(t => t.sku === sku)
  return obj ? obj.idx : 1
}

// Size normalizer (keep in sync with ModelColorCard.vue)
function normalizeSizeCode(raw: string): string {
  if (!raw) return ''
  const s = raw.trim().toUpperCase()
  if (['ONE SIZE', 'OS', 'O/S'].includes(s)) return 'One Size'
  if (s === 'XS') return 'XS'
  if (s === 'S' || s === 'SM') return 'SM'
  if (s === 'M' || s === 'MD') return 'MD'
  if (s === 'L' || s === 'LG') return 'LG'
  if (s === 'XL') return 'XL'
  if (s === 'XXL' || s === '2XL') return 'XXL'
  if (s === 'XXXL' || s === '3XL') return 'XXXL'
  return raw.trim()
}
</script>

<style scoped>
@media print {
  .category-block { break-inside: avoid; page-break-inside: avoid; }
  .category-block h2 { break-after: avoid; page-break-after: avoid; }
  .flex-nowrap { overflow: visible !important; flex-wrap: wrap; }
}
</style>
